import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { CurrencyDollarIcon, HeartStraightIcon } from "@phosphor-icons/react";

const PRESETS = [1, 5, 10, 20, 50, 100];

/**
 * TipSheet — a Stripe-backed direct cash tip modal.
 * Preset amounts + custom + optional message. On submit we create a Stripe
 * checkout session and redirect the viewer to complete payment. 100% of the
 * tip goes to the seller's payout ledger once Stripe confirms.
 *
 * Props:
 *   - streamId, sellerName, user: required context.
 *   - openOverride / onOpenOverrideChange (optional): controlled-open API
 *     for callers that want to open the modal from somewhere else (e.g.
 *     the More bottom-sheet's Tip tile).
 *   - hideTrigger (optional): suppress the built-in green "Tip" pill so
 *     callers can reuse just the modal.
 */
export default function TipSheet({
  streamId,
  sellerName,
  user,
  openOverride,
  onOpenOverrideChange,
  hideTrigger,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = typeof openOverride === "boolean";
  const open = controlled ? openOverride : internalOpen;
  const setOpen = (v) => {
    if (controlled) onOpenOverrideChange?.(v);
    else setInternalOpen(v);
  };
  const [amount, setAmount] = useState(5);
  const [custom, setCustom] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const finalAmount = () => {
    const c = parseFloat(custom);
    if (!isNaN(c) && c > 0) return c;
    return amount;
  };

  const submit = async () => {
    if (!user) {
      toast.error("Sign in to send a tip");
      return;
    }
    const amt = finalAmount();
    if (!amt || amt < 1) {
      toast.error("Minimum tip is $1");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/streams/${streamId}/tip/checkout`, {
        amount_usd: amt,
        message: message.trim(),
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!hideTrigger && (
        <Button
          onClick={() => setOpen(true)}
          className="rounded-full bg-gradient-to-r from-emerald-500 to-green-600 hover:brightness-110 text-white font-bold px-4 shadow-lg gap-2"
          data-testid="tip-sheet-toggle"
        >
          <HeartStraightIcon size={16} weight="fill" /> Tip
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="tip-sheet-modal">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tight">
              Send a tip
            </DialogTitle>
            <DialogDescription>
              Support {sellerName || "this seller"} directly. 100% goes to them — we don't take a cut.
            </DialogDescription>
          </DialogHeader>

          {!user ? (
            <div className="text-center py-6">
              <Link
                to="/login"
                className="text-sm font-bold text-[#1D78AB] hover:underline"
                data-testid="tip-signin-prompt"
              >
                Sign in to send a tip
              </Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {PRESETS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => {
                      setAmount(v);
                      setCustom("");
                    }}
                    className={`rounded-2xl border-2 py-3 font-display font-black text-xl transition-colors ${
                      !custom && amount === v
                        ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                        : "border-zinc-200 hover:border-emerald-400 text-zinc-800"
                    }`}
                    data-testid={`tip-preset-${v}`}
                  >
                    ${v}
                  </button>
                ))}
              </div>

              <div className="mt-2">
                <label className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">
                  Custom amount
                </label>
                <div className="relative mt-1">
                  <CurrencyDollarIcon
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
                  />
                  <Input
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="Other amount"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    className="pl-8 rounded-full"
                    data-testid="tip-custom-input"
                  />
                </div>
              </div>

              <div className="mt-2">
                <label className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">
                  Message (optional)
                </label>
                <Textarea
                  maxLength={200}
                  placeholder="Say something nice…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="mt-1 rounded-2xl resize-none"
                  rows={2}
                  data-testid="tip-message-input"
                />
                <p className="text-[10px] text-zinc-400 text-right mt-0.5">
                  {message.length}/200
                </p>
              </div>

              <Button
                onClick={submit}
                disabled={loading}
                className="w-full mt-2 rounded-full h-12 bg-gradient-to-r from-emerald-500 to-green-600 hover:brightness-110 text-white font-black text-lg shadow-lg"
                data-testid="tip-submit-btn"
              >
                {loading
                  ? "Redirecting to Stripe…"
                  : `Send $${finalAmount().toFixed(2)} tip`}
              </Button>

              <p className="text-[11px] text-zinc-500 text-center mt-1">
                Secure checkout via Stripe. You'll return here after paying.
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
